import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { CommonModule } from '@angular/common';

export interface IBreadCrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: false,
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  public breadcrumbs: IBreadCrumb[] = [];
  routerSubscription!: Subscription;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
      });

    // Initial load
    this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private buildBreadCrumb(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: IBreadCrumb[] = []
  ): IBreadCrumb[] {
    const routeConfig = route.routeConfig;

    if (!routeConfig) {
      return breadcrumbs;
    }

    let label = routeConfig.data?.['breadcrumb'] ?? '';
    let path = routeConfig.path ?? '';

    // Handle dynamic routes like ":id"
    const lastRoutePart = path.split('/').pop();
    const isDynamicRoute = lastRoutePart && lastRoutePart.startsWith(':');

    if (isDynamicRoute && route.snapshot) {
      const paramName = lastRoutePart.replace(':', '');
      const paramValue = route.snapshot.params[paramName];
      path = path.replace(lastRoutePart, paramValue);
      label = paramValue;
    }

    const nextUrl = path ? `${url}/${path}` : url;

    const breadcrumb: IBreadCrumb = {
      label: label ? this.translocoService.translate(label) : '',
      url: nextUrl
    };

    const newBreadcrumbs = breadcrumb.label ? [...breadcrumbs, breadcrumb] : [...breadcrumbs];

    if (route.firstChild) {
      return this.buildBreadCrumb(route.firstChild, nextUrl, newBreadcrumbs);
    }

    return newBreadcrumbs;
  }
}
